import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Play, Pause, RotateCcw, Building2, Mail, Lock, Phone, Bot, PhoneForwarded, ArrowRight, Sparkles, CheckCircle2, PhoneCall, Activity, Globe } from 'lucide-react';
// =================== Stage ===================
const STAGE_W = 1280;
const STAGE_H = 720;
// =================== Steps ===================
const STEPS = [{
  id: 'account',
  n: '01',
  title: 'Create Account',
  sub: 'Business setup',
  caption: 'Create your business account.'
}, {
  id: 'number',
  n: '02',
  title: 'Add Number',
  sub: 'Connect line',
  caption: 'Connect your business number.'
}, {
  id: 'agent',
  n: '03',
  title: 'Configure Agent',
  sub: 'Set behavior',
  caption: 'Configure your AI agent.'
}, {
  id: 'forward',
  n: '04',
  title: 'Forward Calls',
  sub: 'Activate',
  caption: 'Forward your calls to Vexu.'
}, {
  id: 'live',
  n: '05',
  title: 'Go Live',
  sub: 'AI is active',
  caption: 'Your AI agent is live.'
}] as const;
const STEP_DURATION = 5.5;
const INTRO_DURATION = 1.6;
const IMPACT_DURATION = 6;
const TOTAL_DURATION = INTRO_DURATION + STEPS.length * STEP_DURATION + IMPACT_DURATION;
const T = {
  STEPS_START: INTRO_DURATION,
  IMPACT_START: INTRO_DURATION + STEPS.length * STEP_DURATION,
  IMPACT_LINE_2: INTRO_DURATION + STEPS.length * STEP_DURATION + 3.0
};
// Micro tags per step
const STEP_TAGS: Record<string, string[]> = {
  account: ['No technical setup', 'Start in minutes'],
  number: ['Use your existing number', 'No hardware required'],
  agent: ['Fully customizable', 'Smart by default'],
  forward: ['One simple step', 'Instant activation'],
  live: ['Always-on', 'Real-time analytics']
};
// =================== Main ===================
export const SLIDE_3_DURATION = TOTAL_DURATION;
export function PresentationSlide3({
  externalElapsed,
  hideChrome
}: {
  externalElapsed?: number;
  hideChrome?: boolean;
} = {}) {
  const isControlled = externalElapsed !== undefined;
  const [runKey, setRunKey] = useState(0);
  const [playing, setPlaying] = useState(true);
  const [internalElapsed, setElapsed] = useState(0);
  const startedAtRef = useRef<number>(Date.now());
  const pausedAtRef = useRef<number>(0);
  useEffect(() => {
    if (isControlled) return;
    startedAtRef.current = Date.now();
    pausedAtRef.current = 0;
    setElapsed(0);
  }, [runKey, isControlled]);
  useEffect(() => {
    if (isControlled) return;
    if (!playing) {
      pausedAtRef.current = internalElapsed;
      return;
    }
    startedAtRef.current = Date.now() - pausedAtRef.current * 1000;
    let raf: number;
    const tick = () => {
      const t = (Date.now() - startedAtRef.current) / 1000;
      setElapsed(Math.min(t, TOTAL_DURATION));
      if (t < TOTAL_DURATION) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [playing, runKey, isControlled]);
  const elapsed = isControlled ? Math.max(0, Math.min(externalElapsed!, TOTAL_DURATION)) : internalElapsed;
  // Determine current step
  const inIntro = elapsed < T.STEPS_START;
  const inImpact = elapsed >= T.IMPACT_START;
  const stepIndex = inIntro ? -1 : inImpact ? STEPS.length : Math.min(STEPS.length - 1, Math.floor((elapsed - T.STEPS_START) / STEP_DURATION));
  const stepLocalElapsed = inIntro || inImpact ? 0 : elapsed - T.STEPS_START - stepIndex * STEP_DURATION;
  const currentStep = STEPS[Math.max(0, Math.min(STEPS.length - 1, stepIndex))];
  return <div className="relative w-screen h-screen overflow-hidden bg-[#06080F] text-white font-sans flex items-center justify-center" data-id="element-1566">
      {/* Backgrounds */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#06080F] via-[#0A0F1F] to-[#06080F]" data-id="element-1567" />
      <motion.div className="absolute inset-0" animate={{
      background: ['radial-gradient(circle at 30% 40%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)', 'radial-gradient(circle at 70% 30%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 30% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)', 'radial-gradient(circle at 30% 40%, rgba(16,217,106,0.10) 0%, transparent 55%), radial-gradient(circle at 70% 70%, rgba(34,211,238,0.06) 0%, transparent 50%)']
    }} transition={{
      duration: 14,
      repeat: Infinity,
      ease: 'easeInOut'
    }} data-id="element-1568" />
      <div className="absolute inset-0 opacity-[0.10]" style={{
      backgroundImage: 'radial-gradient(circle, rgba(255,255,255,0.18) 1px, transparent 1px)',
      backgroundSize: '36px 36px'
    }} data-id="element-1569" />
      <div className="absolute inset-0 pointer-events-none" style={{
      background: 'radial-gradient(ellipse at center, transparent 45%, rgba(0,0,0,0.7) 100%)'
    }} data-id="element-1570" />

      {/* Top controls */}
      {!hideChrome && <div className="absolute top-6 right-6 z-50 flex items-center gap-2" data-id="element-1571">
          <button onClick={() => setPlaying(p => !p)} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1572">
            {playing ? <>
                <Pause className="w-3.5 h-3.5" data-id="element-1573" /> Pause
              </> : <>
                <Play className="w-3.5 h-3.5" data-id="element-1574" /> Play
              </>}
          </button>
          <button onClick={() => {
        setRunKey(k => k + 1);
        setPlaying(true);
      }} className="flex items-center gap-2 px-4 py-2.5 bg-white/[0.05] hover:bg-white/[0.1] backdrop-blur-xl border border-white/10 rounded-full text-xs font-semibold text-white/80 hover:text-white transition-all" data-id="element-1575">
            <RotateCcw className="w-3.5 h-3.5" data-id="element-1576" /> Replay
          </button>
        </div>}

      {/* Chapter indicator (hidden) */}
      <motion.div initial={{
      opacity: 0,
      x: -12
    }} animate={{
      opacity: 1,
      x: 0
    }} transition={{
      duration: 0.6
    }} className="absolute top-6 left-6 z-50 hidden" data-id="element-1577">
        <div className="flex items-center gap-3" data-id="element-1578">
          <div className="w-8 h-px bg-emerald-400/70" data-id="element-1579" />
          <div data-id="element-1580">
            <div className="text-[10px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1581">
              Slide 03 · Setup in 5 steps
            </div>
            <div className="text-sm font-display text-white/90 tracking-tight mt-0.5" data-id="element-1582">
              How easy is VEXU?
            </div>
          </div>
        </div>
      </motion.div>

      {/* INTRO TAKEOVER */}
      <AnimatePresence data-id="element-1583">
        {inIntro && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0,
        scale: 0.96,
        filter: 'blur(8px)'
      }} transition={{
        duration: 0.7
      }} className="absolute inset-0 flex flex-col items-center justify-center z-30 px-8" data-id="element-1584">
            <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1,
          duration: 0.7
        }} className="flex items-center gap-3 mb-8" data-id="element-1585">
              <div className="w-8 h-px bg-emerald-400/60" data-id="element-1586" />
              <span className="text-[11px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1587">
                Setup
              </span>
              <div className="w-8 h-px bg-emerald-400/60" data-id="element-1588" />
            </motion.div>
            <motion.h1 initial={{
          opacity: 0,
          y: 20,
          filter: 'blur(10px)'
        }} animate={{
          opacity: 1,
          y: 0,
          filter: 'blur(0px)'
        }} transition={{
          delay: 0.2,
          duration: 0.9,
          ease: [0.16, 1, 0.3, 1]
        }} className="font-display text-5xl md:text-6xl lg:text-7xl text-white text-center tracking-tight font-medium" data-id="element-1589">
              5 steps.{' '}
              <span className="font-bold" style={{
            background: 'linear-gradient(135deg, #10D96A 0%, #6EE7B7 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }} data-id="element-1590">
                You're live.
              </span>
            </motion.h1>
          </motion.div>}
      </AnimatePresence>

      {/* STEPS LAYOUT */}
      {!inIntro && !inImpact && <Stage stepIndex={stepIndex} stepLocalElapsed={stepLocalElapsed} currentStep={currentStep} data-id="element-1591" />}

      {/* IMPACT TAKEOVER */}
      <AnimatePresence data-id="element-1592">
        {inImpact && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.9
      }} className="absolute inset-0 flex items-center justify-center z-30 px-8" style={{
        background: 'radial-gradient(circle at center, rgba(6,8,15,0.94) 0%, rgba(6,8,15,0.99) 100%)'
      }} data-id="element-1593">
            <ImpactReveal startTime={T.IMPACT_START} elapsed={elapsed} data-id="element-1594" />
          </motion.div>}
      </AnimatePresence>

      {/* Bottom progress bar */}
      {!hideChrome && <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-50 w-full max-w-2xl px-6" data-id="element-1595">
          <div className="flex items-center gap-2 mb-2.5" data-id="element-1596">
            {STEPS.map((s, i) => {
          const sceneStart = T.STEPS_START + i * STEP_DURATION;
          const sceneEnd = sceneStart + STEP_DURATION;
          const isActive = elapsed >= sceneStart && elapsed < sceneEnd;
          const isDone = elapsed >= sceneEnd;
          return <div key={s.id} className="flex-1 h-1 rounded-full bg-white/[0.08] overflow-hidden" data-id="element-1597">
                  <motion.div className="h-full bg-gradient-to-r from-emerald-400 to-emerald-300" animate={{
              width: isDone ? '100%' : isActive ? `${(elapsed - sceneStart) / STEP_DURATION * 100}%` : '0%'
            }} transition={{
              duration: 0.1,
              ease: 'linear'
            }} data-id="element-1598" />
                </div>;
        })}
          </div>
          <div className="flex items-center justify-between text-[9px] font-bold tracking-[0.25em] text-white/45 uppercase" data-id="element-1599">
            <span data-id="element-1600">Slide 03 · Setup Flow</span>
            <span className="font-mono" data-id="element-1601">
              {fmt(elapsed)} / {fmt(TOTAL_DURATION)}
            </span>
          </div>
        </div>}
    </div>;
}
function fmt(s: number) {
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, '0')}`;
}
// =================== STAGE ===================
function Stage({
  stepIndex,
  stepLocalElapsed,
  currentStep
}: {
  stepIndex: number;
  stepLocalElapsed: number;
  currentStep: (typeof STEPS)[number];
}) {
  return <motion.div initial={{
    opacity: 0,
    scale: 0.96
  }} animate={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.7,
    ease: [0.16, 1, 0.3, 1]
  }} className="relative" style={{
    width: 'min(100vw, calc(100vh * 16 / 9))',
    height: 'min(100vh, calc(100vw * 9 / 16))',
    maxWidth: '100vw',
    maxHeight: '100vh'
  }} data-id="element-1602">
      <div className="absolute inset-0" data-id="element-1603">
        <div className="absolute" style={{
        width: STAGE_W,
        height: STAGE_H,
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%) scale(var(--stage-scale))',
        transformOrigin: 'center center'
      }} data-id="element-1604">
          <ScaleVar data-id="element-1605" />

          {/* TOP CAPTION */}
          <div className="absolute" style={{
          top: 24,
          left: 0,
          right: 0,
          display: 'flex',
          justifyContent: 'center',
          zIndex: 5
        }} data-id="element-1606">
            <AnimatePresence mode="wait" data-id="element-1607">
              <motion.div key={currentStep.id} initial={{
              opacity: 0,
              y: 12,
              filter: 'blur(8px)'
            }} animate={{
              opacity: 1,
              y: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              y: -12,
              filter: 'blur(8px)'
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="px-7 py-2.5 rounded-full bg-white/[0.06] backdrop-blur-2xl border border-white/15 shadow-[0_8px_40px_rgba(0,0,0,0.5)]" data-id="element-1608">
                <p className="font-display text-lg md:text-xl text-white tracking-tight whitespace-nowrap" data-id="element-1609">
                  {currentStep.caption}
                </p>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* STEP RAIL — left side */}
          <div className="absolute" style={{
          left: 70,
          top: 110,
          width: 240,
          bottom: 110
        }} data-id="element-1610">
            <StepRail activeIndex={stepIndex} stepLocalElapsed={stepLocalElapsed} data-id="element-1611" />
          </div>

          {/* MICRO TAGS — beside rail bottom */}
          <div className="absolute" style={{
          left: 70,
          bottom: 40,
          width: 240,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 8
        }} data-id="element-1612">
            <AnimatePresence mode="wait" data-id="element-1613">
              <motion.div key={`tags-${currentStep.id}`} initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.5
            }} className="flex flex-wrap gap-2" data-id="element-1614">
                {(STEP_TAGS[currentStep.id] || []).map((tag, i) => <motion.div key={tag} initial={{
                opacity: 0,
                scale: 0.85
              }} animate={{
                opacity: 1,
                scale: 1
              }} transition={{
                duration: 0.4,
                delay: 0.15 + i * 0.1
              }} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-400/[0.08] border border-emerald-400/25 backdrop-blur-xl" data-id="element-1615">
                    <span className="w-1 h-1 rounded-full bg-emerald-400" data-id="element-1616" />
                    <span className="text-[10px] font-semibold text-emerald-50 tracking-wide whitespace-nowrap" data-id="element-1617">
                      {tag}
                    </span>
                  </motion.div>)}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* APP UI CARD — right side */}
          <div className="absolute" style={{
          left: 360,
          right: 70,
          top: 110,
          bottom: 110
        }} data-id="element-1618">
            <AnimatePresence mode="wait" data-id="element-1619">
              <motion.div key={currentStep.id} initial={{
              opacity: 0,
              x: 30,
              filter: 'blur(8px)'
            }} animate={{
              opacity: 1,
              x: 0,
              filter: 'blur(0px)'
            }} exit={{
              opacity: 0,
              x: -30,
              filter: 'blur(8px)'
            }} transition={{
              duration: 0.55,
              ease: [0.16, 1, 0.3, 1]
            }} className="w-full h-full" data-id="element-1620">
                <AppFrame stepId={currentStep.id} t={stepLocalElapsed} data-id="element-1621" />
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </motion.div>;
}
// =================== STEP RAIL ===================
function StepRail({
  activeIndex,
  stepLocalElapsed
}: {
  activeIndex: number;
  stepLocalElapsed: number;
}) {
  return <div className="relative w-full h-full" data-id="element-1622">
      {/* vertical connector line */}
      <div className="absolute left-[18px] top-2 bottom-2 w-px bg-white/10" data-id="element-1623" />
      {/* progress fill */}
      <div className="absolute left-[18px] top-2 w-px bg-emerald-400 transition-all duration-500" style={{
      height: `${Math.min(100, (activeIndex + Math.min(1, stepLocalElapsed / STEP_DURATION)) / STEPS.length * 100)}%`
    }} data-id="element-1624" />
      <div className="relative space-y-7" data-id="element-1625">
        {STEPS.map((step, i) => {
        const isDone = i < activeIndex;
        const isActive = i === activeIndex;
        return <motion.div key={step.id} initial={{
          opacity: 0,
          x: -12
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: i * 0.05
        }} className="flex items-start gap-4" data-id="element-1626">
              <div className="relative shrink-0" data-id="element-1627">
                <motion.div animate={{
              backgroundColor: isDone || isActive ? '#10D96A' : 'rgba(255,255,255,0.04)',
              borderColor: isDone || isActive ? '#10D96A' : 'rgba(255,255,255,0.15)',
              boxShadow: isActive ? '0 0 24px rgba(16,217,106,0.6)' : '0 0 0px rgba(16,217,106,0)'
            }} transition={{
              duration: 0.5
            }} className="w-9 h-9 rounded-full border flex items-center justify-center" data-id="element-1628">
                  {isDone ? <Check className="w-4 h-4 text-white" strokeWidth={3} data-id="element-1629" /> : <span className={`text-[11px] font-bold tracking-wider ${isActive ? 'text-white' : 'text-white/55'}`} data-id="element-1630">
                      {step.n}
                    </span>}
                </motion.div>
                {isActive && <motion.div animate={{
              scale: [1, 1.4, 1],
              opacity: [0.6, 0, 0.6]
            }} transition={{
              duration: 1.8,
              repeat: Infinity,
              ease: 'easeOut'
            }} className="absolute inset-0 rounded-full border border-emerald-400/60" data-id="element-1631" />}
              </div>
              <div className="pt-1" data-id="element-1632">
                <div className={`text-[12px] font-bold tracking-tight ${isActive ? 'text-white' : isDone ? 'text-white/85' : 'text-white/45'}`} data-id="element-1633">
                  {step.title}
                </div>
                <div className={`text-[10px] font-medium tracking-wide mt-0.5 ${isActive ? 'text-emerald-300/90' : 'text-white/35'}`} data-id="element-1634">
                  {step.sub}
                </div>
              </div>
            </motion.div>;
      })}
      </div>
    </div>;
}
// =================== APP FRAME (window chrome) ===================
function AppFrame({
  stepId,
  t
}: {
  stepId: string;
  t: number;
}) {
  return <div className="relative w-full h-full rounded-2xl bg-[#0A0F1F]/95 backdrop-blur-2xl border border-white/10 shadow-[0_24px_80px_rgba(0,0,0,0.7)] overflow-hidden" data-id="element-1635">
      {/* Soft halo */}
      <div className="absolute -inset-6 bg-emerald-500/10 rounded-3xl blur-2xl pointer-events-none" data-id="element-1636" />

      {/* Window chrome */}
      <div className="relative flex items-center gap-2 px-5 py-3.5 border-b border-white/[0.06]" data-id="element-1637">
        <div className="flex items-center gap-1.5" data-id="element-1638">
          <div className="w-2.5 h-2.5 rounded-full bg-white/15" data-id="element-1639" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/15" data-id="element-1640" />
          <div className="w-2.5 h-2.5 rounded-full bg-white/15" data-id="element-1641" />
        </div>
        <div className="flex-1 flex justify-center" data-id="element-1642">
          <div className="px-3 py-1 rounded-md bg-white/[0.04] border border-white/[0.06] text-[10px] font-mono tracking-wide text-white/45" data-id="element-1643">
            app.vexu.ai
          </div>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] font-bold tracking-[0.18em] text-emerald-400 uppercase" data-id="element-1644">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-1645" />{' '}
          Live
        </div>
      </div>

      {/* Content */}
      <div className="relative p-8 h-[calc(100%-52px)]" data-id="element-1646">
        {stepId === 'account' && <ScreenAccount t={t} data-id="element-1647" />}
        {stepId === 'number' && <ScreenNumber t={t} data-id="element-1648" />}
        {stepId === 'agent' && <ScreenAgent t={t} data-id="element-1649" />}
        {stepId === 'forward' && <ScreenForward t={t} data-id="element-1650" />}
        {stepId === 'live' && <ScreenLive t={t} data-id="element-1651" />}
      </div>
    </div>;
}
// =================== SCREENS ===================
function ScreenAccount({
  t
}: {
  t: number;
}) {
  return <div className="h-full flex flex-col" data-id="element-1652">
      <div className="mb-6" data-id="element-1653">
        <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-400 uppercase mb-1.5" data-id="element-1654">
          Step 01
        </div>
        <div className="text-2xl font-display font-semibold text-white tracking-tight" data-id="element-1655">
          Create your account
        </div>
        <div className="text-sm text-white/55 mt-1" data-id="element-1656">
          Get started in seconds.
        </div>
      </div>

      <div className="space-y-4 max-w-xl" data-id="element-1657">
        <FormField icon={<Building2 className="w-4 h-4" data-id="element-1659" />} label="Business name" value="Al Falah Group" reveal={t > 0.3} duration={1.0} data-id="element-1658" />
        <FormField icon={<Mail className="w-4 h-4" data-id="element-1661" />} label="Work email" value="founder@alfalah.ae" reveal={t > 1.4} duration={0.9} data-id="element-1660" />
        <FormField icon={<Lock className="w-4 h-4" data-id="element-1663" />} label="Password" value="••••••••••••" reveal={t > 2.4} duration={0.7} data-id="element-1662" />
      </div>

      <div className="mt-auto pt-6 max-w-xl" data-id="element-1664">
        <PrimaryButton label="Create Account" pressed={t > 3.6} success={t > 4.2} successLabel="Account created" data-id="element-1665" />
      </div>
    </div>;
}
function ScreenNumber({
  t
}: {
  t: number;
}) {
  return <div className="h-full flex flex-col" data-id="element-1666">
      <div className="mb-6" data-id="element-1667">
        <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-400 uppercase mb-1.5" data-id="element-1668">
          Step 02
        </div>
        <div className="text-2xl font-display font-semibold text-white tracking-tight" data-id="element-1669">
          Add your business number
        </div>
        <div className="text-sm text-white/55 mt-1" data-id="element-1670">
          Use your existing number — no carrier change needed.
        </div>
      </div>

      <div className="space-y-4 max-w-xl" data-id="element-1671">
        <div className="flex items-center gap-3" data-id="element-1672">
          {/* Country selector */}
          <motion.div initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: t > 0.3 ? 1 : 0,
          y: t > 0.3 ? 0 : 6
        }} transition={{
          duration: 0.5
        }} className="flex items-center gap-2 px-3 py-3.5 rounded-xl bg-white/[0.04] border border-white/10 min-w-[110px]" data-id="element-1673">
            <Globe className="w-4 h-4 text-white/55" data-id="element-1674" />
            <span className="text-[13px] font-semibold text-white" data-id="element-1675">+1 US</span>
          </motion.div>
          <div className="flex-1" data-id="element-1676">
            <FormField icon={<Phone className="w-4 h-4" data-id="element-1678" />} label="Phone number" value="(415) 555-0142" reveal={t > 0.8} duration={1.1} flat data-id="element-1677" />
          </div>
        </div>
      </div>

      <div className="mt-auto pt-6 max-w-xl" data-id="element-1679">
        <PrimaryButton label="Add Number" pressed={t > 2.6} success={t > 3.2} successLabel="Number verified ✓" data-id="element-1680" />
      </div>
    </div>;
}
function ScreenAgent({
  t
}: {
  t: number;
}) {
  return <div className="h-full flex flex-col" data-id="element-1681">
      <div className="mb-5" data-id="element-1682">
        <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-400 uppercase mb-1.5" data-id="element-1683">
          Step 03
        </div>
        <div className="text-2xl font-display font-semibold text-white tracking-tight" data-id="element-1684">
          Configure your AI agent
        </div>
        <div className="text-sm text-white/55 mt-1" data-id="element-1685">
          Smart by default. Customize anything.
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 max-w-3xl" data-id="element-1686">
        <FormField icon={<Bot className="w-4 h-4" data-id="element-1688" />} label="Agent name" value="Aria" reveal={t > 0.3} duration={0.6} data-id="element-1687" />
        <FormField icon={<Sparkles className="w-4 h-4" data-id="element-1690" />} label="Greeting" value="Marhaba! Welcome to Al Falah. How can I help?" reveal={t > 1.0} duration={1.4} data-id="element-1689" />
        <SelectField label="Behavior" value="Friendly · Professional" reveal={t > 2.4} data-id="element-1691" />
        <SelectField label="Workflow trigger" value="On incoming call · 24/7" reveal={t > 3.0} data-id="element-1692" />
      </div>

      <div className="mt-auto pt-5 max-w-3xl" data-id="element-1693">
        <PrimaryButton label="Save Configuration" pressed={t > 4.0} success={t > 4.5} successLabel="Agent ready" data-id="element-1694" />
      </div>
    </div>;
}
function ScreenForward({
  t
}: {
  t: number;
}) {
  return <div className="h-full flex flex-col" data-id="element-1695">
      <div className="mb-5" data-id="element-1696">
        <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-400 uppercase mb-1.5" data-id="element-1697">
          Step 04
        </div>
        <div className="text-2xl font-display font-semibold text-white tracking-tight" data-id="element-1698">
          Forward your calls
        </div>
        <div className="text-sm text-white/55 mt-1" data-id="element-1699">
          One simple step to go live.
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center" data-id="element-1700">
        <div className="flex items-center gap-7 w-full max-w-3xl" data-id="element-1701">
          {/* Your Number */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="flex-1 px-5 py-5 rounded-2xl bg-white/[0.04] border border-white/10" data-id="element-1702">
            <div className="text-[10px] font-bold tracking-[0.22em] text-white/45 uppercase mb-1.5" data-id="element-1703">
              Your Number
            </div>
            <div className="flex items-center gap-2" data-id="element-1704">
              <Phone className="w-4 h-4 text-white/65" data-id="element-1705" />
              <span className="text-lg font-semibold text-white tracking-tight" data-id="element-1706">
                +971 4 555 0142
              </span>
            </div>
          </motion.div>

          {/* Connection arrow */}
          <div className="relative w-32 h-12 flex items-center justify-center" data-id="element-1707">
            <svg viewBox="0 0 120 40" className="absolute inset-0 w-full h-full" style={{
            overflow: 'visible'
          }} data-id="element-1708">
              <line x1="0" y1="20" x2="120" y2="20" stroke="#1F2937" strokeWidth={2} strokeDasharray="4 4" data-id="element-1709" />
              <motion.line x1="0" y1="20" x2="120" y2="20" stroke="#10D96A" strokeWidth={2.5} strokeLinecap="round" strokeDasharray={120} initial={{
              strokeDashoffset: 120,
              opacity: 0
            }} animate={{
              strokeDashoffset: t > 1.2 ? 0 : 120,
              opacity: t > 1.2 ? 1 : 0
            }} transition={{
              duration: 1.2,
              ease: [0.16, 1, 0.3, 1]
            }} data-id="element-1710" />
              {/* Traveling pulse */}
              {t > 2.4 && <motion.circle r={4} fill="#10D96A" animate={{
              cx: [0, 120],
              opacity: [0, 1, 1, 0]
            }} transition={{
              duration: 1.6,
              repeat: Infinity,
              ease: 'easeInOut'
            }} cy={20} data-id="element-1711" />}
            </svg>
            <div className="relative z-10 px-2.5 py-1 rounded-full bg-[#0A0F1F] border border-emerald-400/40 flex items-center gap-1.5" data-id="element-1712">
              <ArrowRight className="w-3 h-3 text-emerald-400" data-id="element-1713" />
              <span className="text-[9px] font-bold tracking-[0.18em] text-emerald-300 uppercase" data-id="element-1714">
                Forward
              </span>
            </div>
          </div>

          {/* VEXU Number */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.6
        }} className="flex-1 relative" data-id="element-1715">
            <div className="absolute -inset-3 bg-emerald-500/15 rounded-2xl blur-xl" data-id="element-1716" />
            <div className="relative px-5 py-5 rounded-2xl bg-gradient-to-br from-emerald-500/15 to-emerald-700/10 border border-emerald-400/40" data-id="element-1717">
              <div className="text-[10px] font-bold tracking-[0.22em] text-emerald-300 uppercase mb-1.5 flex items-center gap-1.5" data-id="element-1718">
                <Sparkles className="w-3 h-3" data-id="element-1719" /> VEXU Number
              </div>
              <div className="flex items-center gap-2" data-id="element-1720">
                <PhoneForwarded className="w-4 h-4 text-emerald-300" data-id="element-1721" />
                <span className="text-lg font-semibold text-white tracking-tight" data-id="element-1722">
                  800 VEXU (8388)
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="mt-auto pt-2 max-w-3xl" data-id="element-1723">
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: t > 3.4 ? 1 : 0,
        y: t > 3.4 ? 0 : 10
      }} transition={{
        duration: 0.6
      }} className="flex items-center gap-2 text-emerald-300 text-sm font-semibold" data-id="element-1724">
          <CheckCircle2 className="w-4 h-4" data-id="element-1725" /> Forwarding active — calls will
          route through VEXU.
        </motion.div>
      </div>
    </div>;
}
function ScreenLive({
  t
}: {
  t: number;
}) {
  const incomingCallVisible = t > 1.6;
  const aiHandling = t > 3.2;
  return <div className="h-full flex flex-col" data-id="element-1726">
      <div className="mb-5" data-id="element-1727">
        <div className="text-[10px] font-bold tracking-[0.25em] text-emerald-400 uppercase mb-1.5" data-id="element-1728">
          Step 05
        </div>
        <div className="text-2xl font-display font-semibold text-white tracking-tight" data-id="element-1729">
          You're live
        </div>
        <div className="text-sm text-white/55 mt-1" data-id="element-1730">
          Real-time dashboard · always on.
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4" data-id="element-1731">
        {/* Big Active card */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="col-span-2 relative px-6 py-5 rounded-2xl border border-emerald-400/35 bg-gradient-to-br from-emerald-500/12 to-emerald-700/5 overflow-hidden" data-id="element-1732">
          <div className="absolute -inset-3 bg-emerald-500/10 blur-xl rounded-3xl pointer-events-none" data-id="element-1733" />
          <div className="relative flex items-center gap-4" data-id="element-1734">
            <motion.div animate={{
            scale: [1, 1.18, 1],
            opacity: [1, 0.7, 1]
          }} transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-12 h-12 rounded-2xl bg-emerald-500/20 border border-emerald-400/40 flex items-center justify-center" data-id="element-1735">
              <Bot className="w-6 h-6 text-emerald-300" strokeWidth={2.2} data-id="element-1736" />
            </motion.div>
            <div className="flex-1" data-id="element-1737">
              <div className="text-[10px] font-bold tracking-[0.22em] text-emerald-300 uppercase mb-0.5" data-id="element-1738">
                AI Agent
              </div>
              <div className="text-2xl font-display font-bold text-white tracking-tight" data-id="element-1739">
                Active
              </div>
              <div className="text-[11px] text-white/50 font-medium tracking-wide mt-0.5" data-id="element-1740">
                All systems nominal · Latency 90ms
              </div>
            </div>
            <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/35" data-id="element-1741">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" data-id="element-1742" />
              <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-1743">
                Online
              </span>
            </div>
          </div>
        </motion.div>

        {/* Stats */}
        <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        delay: 0.1
      }} className="px-4 py-4 rounded-2xl bg-white/[0.04] border border-white/10" data-id="element-1744">
          <div className="text-[10px] font-bold tracking-[0.22em] text-white/45 uppercase mb-1" data-id="element-1745">
            Calls today
          </div>
          <div className="flex items-baseline gap-1.5" data-id="element-1746">
            <Activity className="w-3.5 h-3.5 text-emerald-300" data-id="element-1747" />
            <span className="text-2xl font-bold text-white tracking-tight" data-id="element-1748">
              128
            </span>
            <span className="text-[10px] font-semibold text-emerald-300" data-id="element-1749">
              +12%
            </span>
          </div>
          <div className="mt-2.5 h-1 rounded-full bg-white/[0.06] overflow-hidden" data-id="element-1750">
            <motion.div initial={{
            width: 0
          }} animate={{
            width: '72%'
          }} transition={{
            duration: 1.4,
            delay: 0.3
          }} className="h-full bg-emerald-400" data-id="element-1751" />
          </div>
        </motion.div>
      </div>

      {/* Incoming call event */}
      <div className="mt-5 flex-1" data-id="element-1752">
        <div className="text-[10px] font-bold tracking-[0.22em] text-white/45 uppercase mb-3" data-id="element-1753">
          Live Activity
        </div>
        <AnimatePresence data-id="element-1754">
          {incomingCallVisible && <motion.div key="call" initial={{
          opacity: 0,
          y: 14,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.6,
          ease: [0.16, 1, 0.3, 1]
        }} className="relative flex items-center gap-4 px-5 py-4 rounded-2xl bg-[#0F172A]/95 border border-white/10" data-id="element-1755">
              <motion.div animate={{
            scale: [1, 1.18, 1],
            opacity: [1, 0.6, 1]
          }} transition={{
            duration: 1.4,
            repeat: Infinity
          }} className="w-10 h-10 rounded-xl bg-emerald-500/15 border border-emerald-400/30 flex items-center justify-center shrink-0" data-id="element-1756">
                <PhoneCall className="w-5 h-5 text-emerald-300" data-id="element-1757" />
              </motion.div>
              <div className="flex-1 min-w-0" data-id="element-1758">
                <div className="text-[11px] font-bold tracking-[0.18em] text-white/55 uppercase mb-0.5" data-id="element-1759">
                  Incoming call
                </div>
                <div className="text-base font-semibold text-white tracking-tight" data-id="element-1760">
                  +971 50 123 4567
                </div>
              </div>
              <AnimatePresence data-id="element-1761">
                {!aiHandling ? <motion.div key="ringing" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-400/10 border border-amber-400/30" data-id="element-1762">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" data-id="element-1763" />
                    <span className="text-[10px] font-bold tracking-[0.18em] text-amber-200 uppercase" data-id="element-1764">
                      Ringing
                    </span>
                  </motion.div> : <motion.div key="handling" initial={{
              opacity: 0,
              scale: 0.9
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.5
            }} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-500/15 border border-emerald-400/35" data-id="element-1765">
                    <CheckCircle2 className="w-3 h-3 text-emerald-300" data-id="element-1766" />
                    <span className="text-[10px] font-bold tracking-[0.18em] text-emerald-200 uppercase" data-id="element-1767">
                      AI handling
                    </span>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
// =================== Form primitives ===================
function FormField({
  icon,
  label,
  value,
  reveal,
  duration,
  flat
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  reveal: boolean;
  duration: number;
  flat?: boolean;
}) {
  // Typewriter-ish reveal: show progressively more characters as time passes
  const [shown, setShown] = useState('');
  useEffect(() => {
    if (!reveal) {
      setShown('');
      return;
    }
    const start = Date.now();
    let raf: number;
    const tick = () => {
      const t = (Date.now() - start) / 1000;
      const ratio = Math.max(0, Math.min(1, t / duration));
      const n = Math.floor(value.length * ratio);
      setShown(value.slice(0, n));
      if (ratio < 1) raf = requestAnimationFrame(tick);else setShown(value);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [reveal, value, duration]);
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: reveal ? 1 : 0.4,
    y: reveal ? 0 : 8
  }} transition={{
    duration: 0.5
  }} data-id="element-1768">
      {!flat && <div className="text-[10px] font-bold tracking-[0.18em] text-white/45 uppercase mb-1.5" data-id="element-1769">
          {label}
        </div>}
      <div className={`flex items-center gap-2.5 px-3.5 py-3 rounded-xl bg-white/[0.04] border ${reveal ? 'border-emerald-400/30 shadow-[0_0_18px_rgba(16,217,106,0.12)]' : 'border-white/10'} transition-colors`} data-id="element-1770">
        <span className="text-white/55" data-id="element-1771">{icon}</span>
        <span className="text-[13px] font-semibold text-white tracking-tight flex-1 truncate" data-id="element-1772">
          {shown}
          {reveal && shown.length < value.length && <span className="inline-block w-[2px] h-3.5 bg-emerald-400 align-middle ml-0.5 animate-pulse" data-id="element-1773" />}
        </span>
      </div>
    </motion.div>;
}
function SelectField({
  label,
  value,
  reveal
}: {
  label: string;
  value: string;
  reveal: boolean;
}) {
  return <motion.div initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: reveal ? 1 : 0.4,
    y: reveal ? 0 : 8
  }} transition={{
    duration: 0.5
  }} data-id="element-1774">
      <div className="text-[10px] font-bold tracking-[0.18em] text-white/45 uppercase mb-1.5" data-id="element-1775">
        {label}
      </div>
      <div className={`flex items-center justify-between gap-2 px-3.5 py-3 rounded-xl bg-white/[0.04] border ${reveal ? 'border-emerald-400/30' : 'border-white/10'} transition-colors`} data-id="element-1776">
        <span className="text-[13px] font-semibold text-white tracking-tight" data-id="element-1777">
          {value}
        </span>
        <span className="text-white/35 text-xs" data-id="element-1778">▾</span>
      </div>
    </motion.div>;
}
function PrimaryButton({
  label,
  pressed,
  success,
  successLabel
}: {
  label: string;
  pressed: boolean;
  success: boolean;
  successLabel: string;
}) {
  return <div className="relative" data-id="element-1779">
      <motion.button animate={{
      scale: pressed && !success ? 0.97 : 1,
      backgroundColor: success ? 'rgba(16,217,106,0.18)' : '#10D96A',
      borderColor: success ? 'rgba(110,231,183,0.5)' : 'rgba(110,231,183,0.4)'
    }} transition={{
      duration: 0.4
    }} className="w-full px-5 py-3.5 rounded-xl border font-semibold text-[14px] text-white tracking-tight shadow-[0_8px_30px_rgba(16,217,106,0.35)] flex items-center justify-center gap-2" data-id="element-1780">
        <AnimatePresence mode="wait" data-id="element-1781">
          {!success ? <motion.span key="label" initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.25
        }} data-id="element-1782">
              {label}
            </motion.span> : <motion.span key="success" initial={{
          opacity: 0,
          y: 4
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -4
        }} transition={{
          duration: 0.3
        }} className="flex items-center gap-2 text-emerald-200" data-id="element-1783">
              <CheckCircle2 className="w-4 h-4" data-id="element-1784" /> {successLabel}
            </motion.span>}
        </AnimatePresence>
      </motion.button>
    </div>;
}
// =================== IMPACT REVEAL ===================
function ImpactReveal({
  startTime,
  elapsed
}: {
  startTime: number;
  elapsed: number;
}) {
  const t = elapsed - startTime;
  const negatives = [{
    text: 'No PBX.',
    show: 0.0
  }, {
    text: 'No integrations.',
    show: 0.7
  }, {
    text: 'No complexity.',
    show: 1.4
  }];
  return <div className="text-center px-6" data-id="element-1785">
      <motion.div initial={{
      opacity: 0,
      y: -10
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.7
    }} className="flex items-center gap-2 justify-center mb-8" data-id="element-1786">
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" data-id="element-1787" />
        <span className="text-[11px] font-bold tracking-[0.3em] text-emerald-400 uppercase" data-id="element-1788">
          The bottom line
        </span>
        <Sparkles className="w-3.5 h-3.5 text-emerald-400" data-id="element-1789" />
      </motion.div>

      <div className="flex items-baseline justify-center gap-4 md:gap-6 flex-wrap mb-12" data-id="element-1790">
        {negatives.map((w, i) => {
        const visible = t >= w.show;
        return <motion.span key={i} initial={{
          opacity: 0,
          y: 24,
          scale: 0.9,
          filter: 'blur(10px)'
        }} animate={{
          opacity: visible ? 1 : 0,
          y: visible ? 0 : 24,
          scale: visible ? 1 : 0.9,
          filter: visible ? 'blur(0px)' : 'blur(10px)'
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-white" style={{
          textShadow: '0 0 30px rgba(16,217,106,0.4)'
        }} data-id="element-1791">
              {w.text}
            </motion.span>;
      })}
      </div>

      <div className="flex items-baseline justify-center gap-3 md:gap-5 flex-wrap" data-id="element-1792">
        <motion.span initial={{
        opacity: 0,
        y: 16,
        filter: 'blur(8px)'
      }} animate={{
        opacity: t >= 2.4 ? 1 : 0,
        y: t >= 2.4 ? 0 : 16,
        filter: t >= 2.4 ? 'blur(0px)' : 'blur(8px)'
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} className="font-display text-3xl md:text-4xl text-white/60 italic tracking-tight" data-id="element-1793">
          Just plug in…
        </motion.span>
        <motion.span initial={{
        opacity: 0,
        y: 16,
        filter: 'blur(8px)'
      }} animate={{
        opacity: t >= 3.4 ? 1 : 0,
        y: t >= 3.4 ? 0 : 16,
        filter: t >= 3.4 ? 'blur(0px)' : 'blur(8px)'
      }} transition={{
        duration: 0.9,
        ease: [0.16, 1, 0.3, 1]
      }} className="font-display text-4xl md:text-5xl font-bold tracking-tight" style={{
        background: 'linear-gradient(135deg, #10D96A 0%, #6EE7B7 60%, #ffffff 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text'
      }} data-id="element-1794">
          …and go live.
        </motion.span>
      </div>
    </div>;
}
// =================== ScaleVar ===================
function ScaleVar() {
  useEffect(() => {
    const update = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const scale = Math.min(w / STAGE_W, h / STAGE_H, 1);
      document.documentElement.style.setProperty('--stage-scale', String(scale));
    };
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, []);
  return null;
}